import { createReducer, on } from '@ngrx/store';
import * as SquareCatalogItemActions from './square-catalog-item.actions';
import { CatalogMismatch, SquareCatalogItem } from './square-catalog-item.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface SquareCatalogItemsState {
    squareCatalogItems: SquareCatalogItem[];
    squareCatalogItem: SquareCatalogItem | null;
    catalogMismatch: CatalogMismatch | null;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialSquareCatalogItemsState: SquareCatalogItemsState = {
    squareCatalogItems: [],
    squareCatalogItem: null,
    catalogMismatch: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

export const squareCatalogItemsReducer = createReducer(
    initialSquareCatalogItemsState,

    // On Load All
    on(SquareCatalogItemActions.loadSquareCatalogItems, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareCatalogItemActions.loadSquareCatalogItemsSuccess,
        (state, { squareCatalogItems }) => ({
            ...state,
            isLoading: false,
            squareCatalogItems,
        })
    ),
    on(
        SquareCatalogItemActions.loadSquareCatalogItemsFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // On Load Single
    on(SquareCatalogItemActions.loadSquareCatalogItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareCatalogItemActions.loadSquareCatalogItemSuccess,
        (state, { squareCatalogItem }) => ({
            ...state,
            isLoading: false,
            squareCatalogItem,
        })
    ),
    on(
        SquareCatalogItemActions.loadSquareCatalogItemFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // On Create
    on(SquareCatalogItemActions.createSquareCatalogItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareCatalogItemActions.createSquareCatalogItemSuccess,
        (state, { squareCatalogItem }) => ({
            ...state,
            isLoading: false,
            squareCatalogItems: [
                ...(state.squareCatalogItems || []),
                squareCatalogItem,
            ],
        })
    ),
    on(
        SquareCatalogItemActions.createSquareCatalogItemFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // On Update
    on(SquareCatalogItemActions.updateSquareCatalogItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareCatalogItemActions.updateSquareCatalogItemSuccess,
        (state, { squareCatalogItem }) => ({
            ...state,
            isLoading: false,
            squareCatalogItems: state.squareCatalogItems.map((s) =>
                s.squareId === squareCatalogItem.squareId
                    ? squareCatalogItem
                    : s
            ),
        })
    ),
    on(
        SquareCatalogItemActions.updateSquareCatalogItemFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // On Delete
    on(SquareCatalogItemActions.deleteSquareCatalogItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareCatalogItemActions.deleteSquareCatalogItemSuccess,
        (state, { squareId }) => ({
            ...state,
            isLoading: false,
            squareCatalogItems: state.squareCatalogItems.filter(
                (s) => s.squareId !== squareId
            ),
        })
    ),
    on(
        SquareCatalogItemActions.deleteSquareCatalogItemFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // On Set Catalog Mismatch
    on(
        SquareCatalogItemActions.setCatalogMismatch,
        (state, { catalogMismatch }) => ({
            ...state,
            catalogMismatch,
        })
    ),
    on(SquareCatalogItemActions.clearCatalogMismatch, (state) => ({
        ...state,
        catalogMismatch: null,
    }))
);
